import type { ObjectType, OpenAiDescription } from '../../components/parser/ParserTypes';
import {
  ConfigurationType,
  DependenciesContentViewType,
  UnclassifiedTypes,
  type ObjectWithPillsMap,
  type SelectedDependency,
} from '../../components/documentation/dependencies/types';
import type { DocumentationPills } from '../global/globalReducerTypes';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '..';
import {
  ConfigurationTypeToDocumentationTabType,
  DocumentationTabTypes,
} from '../../types/enums/DocumentationTabTypes';
import {
  ReadOnlyAutomation,
  ReadOnlyDedupMatching,
  ReadOnlyRollup,
} from '@server/read-only-elements.types';
import { getObjectsWithPillsPerCategory } from '../../components/documentation/cpq/utils';
import { formatCamelCase } from '../../components/documentation/universal-search/utils';
import isEqual from 'lodash/isEqual';
import { ConfigurationComment } from '../../components/documentation/dependencies/annotations/comments/types';
import {
  ConfigurationConsumers,
  ConfigurationDependencies,
  ConfigurationTypesWithIds,
} from '../../components/documentation/dependencies/DependenciesTypes';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import { sortLexicographically } from '../../lib/sortLexicographically';
import { translateTypeToHumanName } from '../../components/documentation/helpers';
import { createConfigurationItemFromParserResponseItem } from '../../components/parser/parserUtils';
import { ConfigurationItem } from '../../components/parser/ParserTypes';
import { BaseFieldProperties, ElementProperties } from '@server/documentation';
import { translateNewConfigurationTypeKeysToOldVersion } from '../../components/documentation/dependencies/helpers';

export interface DocumentationState {
  objects: ObjectWithPillsMap;
  transientObjects: { [crmOrgId: string]: { [objectName: string]: boolean } };
  parsedConfigurationItems: {
    [crmOrgId: string]: ConfigurationItem[]; //contains dependsOn/usedBy configuration items
  };
  dependsOnIdsForRules: {
    [crmOrgId: string]: { [ruleMetadataId: string]: ConfigurationTypesWithIds };
  };
  usedByIdsForFields: {
    [crmOrgId: string]: { [fieldMetadataId: string]: ConfigurationTypesWithIds };
  };
  usedByIdsForRules: {
    [crmOrgId: string]: { [ruleMetadataId: string]: ConfigurationTypesWithIds };
  };
  commentsByConfigurationId: {
    [crmOrgId: string]: { [configurationId: string]: ConfigurationComment[] };
  };
  sourceCodeByConfigurationId: {
    [crmOrgId: string]: { [configurationId: string]: string };
  };
  openAIDescriptions: {
    [crmOrgId: string]: { [ruleId: string]: OpenAiDescription };
  };
  dependencies?: SelectedDependency & {
    history: SelectedDependency[];
  };
  sweepElementsLatestDeployments: {
    [sweepElementId: string]: ReadOnlyAutomation | ReadOnlyDedupMatching | ReadOnlyRollup;
  };
  singleObject?: {
    activeTab?: DocumentationTabTypes;
    objectTypeName?: ObjectTypeName;
  };
  activeObjectElementsRawData?: {
    [key in DocumentationTabTypes]: ConfigurationItem[];
  };
  relatedObjectFilterOptions?: { label: string; value: string }[];
  configurationTypeFilterOptions?: { label: string; value: string }[];
  showLoader: boolean;
}

const initialState: DocumentationState = {
  objects: {
    favorites: [],
    standardObjects: [],
    cpqObjects: [],
    customObjects: [],
    platformEvents: [],
  },
  singleObject: undefined,
  transientObjects: {},
  parsedConfigurationItems: {},
  dependsOnIdsForRules: {},
  usedByIdsForFields: {},
  usedByIdsForRules: {},
  openAIDescriptions: {},
  commentsByConfigurationId: {},
  sourceCodeByConfigurationId: {},
  sweepElementsLatestDeployments: {}, //only for sweep elements
  showLoader: false,
};

export const documentationSlice = createSlice({
  name: 'documentation',
  initialState,
  reducers: {
    setObjects: (
      state,
      action: PayloadAction<{
        recordTypesData: RecordTypesData;
        funnelsData: FunnelsData;
        objectTypeNames: ObjectTypeName[];
        pills?: DocumentationPills;
        transientObjects: { [objectName: string]: boolean };
        parsedObjects?: ObjectType[];
      }>,
    ) => {
      const {
        funnelsData,
        recordTypesData,
        objectTypeNames,
        pills,
        transientObjects,
        parsedObjects,
      } = action.payload;

      const {
        cpq,
        favorites,
        customObjectsNotInFM,
        platformEventsNotInFM,
        standardObjectsNotInFunnelMap,
      } = getObjectsWithPillsPerCategory({
        recordTypesData,
        funnelsData,
        objectTypeNames,
        pills,
        transientObjects,
        parsedObjects,
      });

      state.objects = {
        favorites,
        standardObjects: standardObjectsNotInFunnelMap,
        cpqObjects: cpq,
        customObjects: customObjectsNotInFM,
        platformEvents: platformEventsNotInFM,
      };
    },
    setObjectName: (
      state,
      action: PayloadAction<{
        objectTypeName?: ObjectTypeName;
      }>,
    ) => {
      const { objectTypeName } = action.payload;

      state.singleObject = {
        objectTypeName: objectTypeName,
        activeTab: DocumentationTabTypes.CARDS_LIST,
      };

      state.dependencies = undefined;
    },
    setSingleObjectName: (
      state,
      action: PayloadAction<{
        singleObjectName?: ObjectTypeName;
        tab: DocumentationTabTypes;
      }>,
    ) => {
      const { singleObjectName, tab } = action.payload;

      if (!singleObjectName) {
        state.singleObject = undefined;
        return;
      }

      state.singleObject = {
        objectTypeName: singleObjectName,
        activeTab: tab,
      };
    },
    clearDocumentationDialog: (state) => ({
      ...initialState,
      objects: state.objects,
    }),
    openPredefinedStepRulesInSingleObjectScreen: (
      state,
      action: PayloadAction<{
        singleObjectApiName: string;
        defaultActiveTab: DocumentationTabTypes;
        objects?: ObjectTypeName[];
      }>,
    ) => {
      const { singleObjectApiName, defaultActiveTab, objects } = action.payload;

      state.singleObject = {
        objectTypeName: objects?.find((object) => object.objectType === singleObjectApiName),
        activeTab: defaultActiveTab,
      };

      state.dependencies = undefined;
    },
    handleOnPillClick: (
      state,
      action: PayloadAction<{
        tab: DocumentationTabTypes;
        singleObjectName: string;
      }>,
    ) => {
      state.singleObject = {
        objectTypeName: {
          objectType: action.payload.singleObjectName,
          label: action.payload.singleObjectName,
        },
        activeTab: action.payload.tab,
      };
      state.dependencies = undefined;
    },
    setDependenciesConfigurationItem: (
      state,
      action: PayloadAction<{
        id: string;
        parentType: string;
        dependencyType?: string;
        name: string;
        objectName?: string; //objectName is relevant ONLY for fields as they don't have explicit id and field name is unique only within objectName
        clearHistory?: boolean;
        contentType: DependenciesContentViewType;
      }>,
    ) => {
      const { id, parentType, dependencyType, name, objectName, clearHistory, contentType } =
        action.payload;
      const history = clearHistory ? [] : (state.dependencies?.history ?? []);
      state.dependencies = {
        id,
        name,
        parentType,
        dependencyType,
        objectName: objectName ?? '',
        contentType,
        history: [
          ...history,
          { id, parentType, dependencyType, name, objectName: objectName ?? '', contentType },
        ],
      };
    },
    setLatestDeployment: (
      state,
      action: PayloadAction<{
        elementId: string;
        readOnlyElement: ReadOnlyAutomation | ReadOnlyDedupMatching | ReadOnlyRollup;
      }>,
    ) => {
      const { readOnlyElement: readOnlyAutomation, elementId } = action.payload;
      state.sweepElementsLatestDeployments[elementId] = readOnlyAutomation;
    },
    clearDependencies: (state) => {
      state.dependencies = undefined;
      state.sweepElementsLatestDeployments = {};
      state.dependsOnIdsForRules = {};
      state.usedByIdsForFields = {};
      state.usedByIdsForRules = {};
      state.commentsByConfigurationId = {};
      state.sourceCodeByConfigurationId = {};
      state.parsedConfigurationItems = {};
      state.openAIDescriptions = {};
    },
    goBackInHistory: (state) => {
      const dependenciesHistory = state.dependencies?.history ?? [];

      if (state.dependencies && dependenciesHistory?.length > 1) {
        const lastIdx = state.dependencies.history.length - 1;
        state.dependencies.history.splice(lastIdx, 1);

        const prev = state.dependencies.history[lastIdx - 1];

        if (prev) {
          state.dependencies = {
            id: prev.id,
            name: prev.name,
            parentType: prev.parentType,
            dependencyType: prev.dependencyType,
            objectName: prev.objectName,
            history: state.dependencies.history,
            contentType: prev.contentType,
          };
        } else {
          state.dependencies.history = [];
        }
      }
    },
    setTab: (
      state,
      action: PayloadAction<{
        tab: DocumentationTabTypes;
      }>,
    ) => {
      state.singleObject = {
        ...state.singleObject,
        activeTab: action.payload.tab,
      };
      state.dependencies = undefined;
    },
    setConfigurationDependenciesForRule: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        ruleMetadataId: string;
        dependsOn: ConfigurationDependencies;
      }>,
    ) => {
      const { crmOrgId, ruleMetadataId, dependsOn } = action.payload;

      const oldParsedConfigItems = state.parsedConfigurationItems[crmOrgId] ?? [];
      const { parsedConfigurationItemsIdsToType, parsedConfigurationItems, parsedFields } =
        getItemsForDependencies(dependsOn, oldParsedConfigItems);

      state.parsedConfigurationItems[crmOrgId] = [
        ...parsedConfigurationItems,
        ...parsedFields.map((field) => ({ ...field, parentType: ConfigurationType.fields })),
      ];

      const oldDependencyIds = state.dependsOnIdsForRules[crmOrgId];
      state.dependsOnIdsForRules[crmOrgId] = {
        ...oldDependencyIds,
        [ruleMetadataId]: parsedConfigurationItemsIdsToType,
      };
    },
    updateOrAddFieldToParsedConfigurationItems: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        field: BaseFieldProperties;
        objectName: string;
      }>,
    ) => {
      const { crmOrgId, field, objectName } = action.payload;

      let currentParsedConfigurationItems: ConfigurationItem[] =
        state.parsedConfigurationItems[crmOrgId] ?? [];
      const isField = currentParsedConfigurationItems.find((item) => item.id === field.id);

      if (isField) {
        currentParsedConfigurationItems = currentParsedConfigurationItems.map((item) =>
          item.id === field.id && field.objectName === objectName
            ? createConfigurationItemFromParserResponseItem({
                responseItem: field,
                objectApiName: field.objectName,
                keyType: ConfigurationType.fields,
              })
            : item,
        );
      } else {
        currentParsedConfigurationItems.push(
          createConfigurationItemFromParserResponseItem({
            responseItem: field,
            objectApiName: field.objectName,
            keyType: ConfigurationType.fields,
          }),
        );
      }

      state.parsedConfigurationItems[crmOrgId] = currentParsedConfigurationItems;
    },
    addConfigurationItem: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        newConfigurationItem: ConfigurationItem;
      }>,
    ) => {
      const { crmOrgId, newConfigurationItem } = action.payload;

      const oldConfigurationItems = state.parsedConfigurationItems[crmOrgId] ?? [];
      const itemsMap = new Map(oldConfigurationItems.map((item) => [item.metadataId, item]));
      itemsMap.set(newConfigurationItem.metadataId, newConfigurationItem);
      state.parsedConfigurationItems[crmOrgId] = Array.from(itemsMap.values());
    },
    setConfigurationUsedByForField: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        usedBy: ConfigurationConsumers;
        metadataId: string;
      }>,
    ) => {
      const { crmOrgId, usedBy, metadataId } = action.payload;

      const oldParsedConfigItems = state.parsedConfigurationItems[crmOrgId] ?? [];
      const { parsedConfigurationItemsIdsToType, parsedConfigurationItems, parsedFields } =
        getItemsForDependencies(usedBy, oldParsedConfigItems);

      state.parsedConfigurationItems[crmOrgId] = [
        ...parsedConfigurationItems,
        ...parsedFields.map((field) => ({ ...field, parentType: ConfigurationType.fields })),
      ];

      const oldUsedByIds = state.usedByIdsForFields[crmOrgId];

      state.usedByIdsForFields[crmOrgId] = {
        ...oldUsedByIds,
        [metadataId]: parsedConfigurationItemsIdsToType,
      };
    },
    setConfigurationUsedByForRule: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        ruleMetadataId: string;
        usedBy: ConfigurationConsumers;
      }>,
    ) => {
      const { crmOrgId, ruleMetadataId, usedBy } = action.payload;

      const oldParsedConfigItems = state.parsedConfigurationItems[crmOrgId] ?? [];
      const { parsedConfigurationItemsIdsToType, parsedConfigurationItems, parsedFields } =
        getItemsForDependencies(usedBy, oldParsedConfigItems);

      state.parsedConfigurationItems[crmOrgId] = [
        ...parsedConfigurationItems,
        ...parsedFields.map((field) => ({ ...field, parentType: ConfigurationType.fields })),
      ];

      const oldUsedByIds = state.usedByIdsForRules[crmOrgId];
      state.usedByIdsForRules[crmOrgId] = {
        ...oldUsedByIds,
        [ruleMetadataId]: parsedConfigurationItemsIdsToType,
      };
    },
    setOpenAiDescriptionForRule: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        openAiDescription: OpenAiDescription;
        ruleId: string;
      }>,
    ) => {
      const { crmOrgId, openAiDescription, ruleId } = action.payload;

      const oldDescriptions = state.openAIDescriptions[crmOrgId];
      state.openAIDescriptions[crmOrgId] = {
        ...oldDescriptions,
        [ruleId]: openAiDescription,
      };
    },
    addCommentForConfiguration: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        configurationId: string;
        newComment: ConfigurationComment;
      }>,
    ) => {
      const { crmOrgId, configurationId, newComment } = action.payload;

      const oldComments = state.commentsByConfigurationId[crmOrgId];
      const oldConfigurationComments =
        state.commentsByConfigurationId[crmOrgId]?.[configurationId] ?? [];

      state.commentsByConfigurationId[crmOrgId] = {
        ...oldComments,
        [configurationId]: [...oldConfigurationComments, newComment],
      };
    },
    updateCommentForConfiguration: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        configurationId: string;
        newComment: ConfigurationComment;
      }>,
    ) => {
      const { crmOrgId, configurationId, newComment } = action.payload;

      const oldComments = state.commentsByConfigurationId[crmOrgId];
      const oldConfigurationComments: ConfigurationComment[] =
        state.commentsByConfigurationId[crmOrgId]?.[configurationId] ?? [];

      state.commentsByConfigurationId[crmOrgId] = {
        ...oldComments,
        [configurationId]: oldConfigurationComments.map((comment) =>
          comment.id === newComment.id ? newComment : comment,
        ),
      };
    },
    deleteCommentForConfiguration: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        configurationId: string;
        commentId: string;
      }>,
    ) => {
      const { crmOrgId, configurationId, commentId } = action.payload;

      const oldComments = state.commentsByConfigurationId[crmOrgId];
      const oldConfigurationComments: ConfigurationComment[] =
        state.commentsByConfigurationId[crmOrgId]?.[configurationId] ?? [];

      state.commentsByConfigurationId[crmOrgId] = {
        ...oldComments,
        [configurationId]: oldConfigurationComments.filter((comment) => comment.id !== commentId),
      };
    },
    setCommentsForConfiguration: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        configurationId: string;
        comments: ConfigurationComment[];
      }>,
    ) => {
      const { crmOrgId, configurationId, comments } = action.payload;

      const oldComments = state.commentsByConfigurationId[crmOrgId];
      state.commentsByConfigurationId[crmOrgId] = {
        ...oldComments,
        [configurationId]: comments,
      };
    },
    setSourceCodeForConfiguration: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        configurationId: string;
        sourceCode: string;
      }>,
    ) => {
      const { crmOrgId, configurationId, sourceCode } = action.payload;

      const oldConfigs = state.sourceCodeByConfigurationId[crmOrgId];
      state.sourceCodeByConfigurationId[crmOrgId] = {
        ...oldConfigs,
        [configurationId]: sourceCode,
      };
    },
    setSelectedDependencyContentType: (
      state,
      action: PayloadAction<{ contentType: DependenciesContentViewType }>,
    ) => {
      const { contentType } = action.payload;
      const { history, ...selectedItem } =
        state.dependencies ?? ({} as SelectedDependency & { history: SelectedDependency[] });

      state.dependencies = {
        ...selectedItem,
        contentType,
        history: [...(history ?? ([] as SelectedDependency[])), { ...selectedItem, contentType }],
      };
    },
    openEditTag: (
      state,
      action: PayloadAction<{ tagId: string; tagName: string; clearHistory?: boolean }>,
    ) => {
      const { tagId, tagName, clearHistory } = action.payload;
      const { history } =
        state.dependencies ?? ({} as SelectedDependency & { history: SelectedDependency[] });

      state.dependencies = {
        id: tagId,
        name: tagName,
        parentType: UnclassifiedTypes.tag,
        contentType: DependenciesContentViewType.editTag,
        history: [
          ...(clearHistory ? [] : (history ?? ([] as any))),
          {
            id: tagId,
            name: tagName,
            parentType: UnclassifiedTypes.tag,
            contentType: DependenciesContentViewType.editTag,
          },
        ],
      };
    },
    setShowLoader: (state, action: PayloadAction<{ showLoader: boolean }>) => {
      state.showLoader = action.payload.showLoader;
    },
    setTransientObjects: (
      state,
      action: PayloadAction<{
        objectName: string;
        crmOrgId: string;
        isObjectBeingParsed: boolean;
      }>,
    ) => {
      const { crmOrgId, objectName, isObjectBeingParsed } = action.payload;

      if (!state.transientObjects[crmOrgId]) {
        state.transientObjects[crmOrgId] = {};
      }

      state.transientObjects[crmOrgId][objectName] = isObjectBeingParsed;
    },
    setActiveObjectElementsRawData: (
      state,
      action: PayloadAction<{
        list: { [key in DocumentationTabTypes]: ConfigurationItem[] };
      }>,
    ) => {
      const { list } = action.payload;
      state.activeObjectElementsRawData = list;

      const newObjectOptions: { label: string; value: string }[] = uniq(
        flatten(
          list[DocumentationTabTypes.RELATED_OBJECTS]?.map(
            (relatedObject) => relatedObject.objectApiNames ?? [],
          ),
        ),
      )
        .filter((item) => !!item)
        .map((item) => ({ label: formatCamelCase(item ?? ''), value: item }));

      const configurationTypeOptions: { label: string; value: string }[] = uniq(
        flatten(
          list[DocumentationTabTypes.RELATED_OBJECTS]?.map(
            (relatedObject) => relatedObject.parentType,
          ),
        ),
      )
        .filter((item) => !!item)
        .map((item) => ({
          label: translateTypeToHumanName(item),
          value: item,
        }));

      const sortedNewObjectOptions = sortLexicographically({
        items: newObjectOptions,
        sortKey: 'label',
      });

      const sortedConfigurationTypeOptions = sortLexicographically({
        items: configurationTypeOptions,
        sortKey: 'label',
      });

      if (!isEqual(sortedNewObjectOptions, state.relatedObjectFilterOptions)) {
        state.relatedObjectFilterOptions = sortedNewObjectOptions;
      }

      if (!isEqual(sortedConfigurationTypeOptions, state.configurationTypeFilterOptions)) {
        state.configurationTypeFilterOptions = sortedConfigurationTypeOptions;
      }
    },
    openPredefinedConfigurationCard: (
      state,
      action: PayloadAction<{
        objectType: ObjectTypeName;
        configurationName: string;
        configurationType: string;
      }>,
    ) => {
      const { objectType, configurationName, configurationType } = action.payload;

      const documentationTabType =
        ConfigurationTypeToDocumentationTabType[
          configurationType as keyof typeof ConfigurationTypeToDocumentationTabType
        ];

      if (!documentationTabType) {
        return;
      }

      state.singleObject = {
        activeTab: documentationTabType,
        objectTypeName: objectType,
      };

      const type = configurationType as ConfigurationType;

      const id = '';
      const name = configurationName;
      const contentType = DependenciesContentViewType.default;

      state.dependencies = {
        id,
        name,
        parentType: type,
        objectName: objectType.objectType,
        contentType,
        history: [
          {
            id,
            name,
            parentType: type,
            objectName: objectType.objectType,
            contentType,
          },
        ],
      };
    },
  },
});

// helper function to extract ids from ConfigurationConsumers | ConfigurationDependencies
const getItemsForDependencies = (
  values: ConfigurationConsumers | ConfigurationDependencies,
  storedParsedDependencies: ConfigurationItem[],
): {
  parsedConfigurationItemsIdsToType: ConfigurationTypesWithIds;
  parsedConfigurationItems: ConfigurationItem[];
  parsedFields: ConfigurationItem[];
} => {
  let helperArray: ConfigurationItem[] = [];
  const dependenciesIds = {} as ConfigurationTypesWithIds;
  let fields: ConfigurationItem[] = [];

  Object.keys(values).forEach((depKey) => {
    const key = depKey as ConfigurationType;
    //any as workaround,
    // TS is complaining for some of the keys not being valid key for ConfigurationConsumers | ConfigurationDependencies,
    //as Consumers and Dependencies don't contain ALL of the ConfigurationItemType keys
    const _values = values as any;
    const arr: ConfigurationItem[] = _values[key]?.map((item: ElementProperties) => {
      const isOthersType = key === ConfigurationType.others;
      return createConfigurationItemFromParserResponseItem({
        responseItem: {
          ...item,
          type: isOthersType ? translateNewConfigurationTypeKeysToOldVersion(item.type) : item.type,
        },
        objectApiName: item.objectName ?? '',
        keyType: key,
      });
    });

    const idsOnly: string[] = arr.map((item) => item.metadataId);

    dependenciesIds[key] = idsOnly;

    if (depKey === ConfigurationType.fields) {
      fields = fields.concat(arr);
    } else {
      helperArray = helperArray.concat(arr);
    }
  });

  // Use Map for O(1) deduplication instead of O(n²) uniqWith
  const uniqueItems = new Map<string, ConfigurationItem>();

  // Add existing items to map
  storedParsedDependencies.forEach((item) => {
    uniqueItems.set(item.metadataId, item);
  });

  // Add new items to map (will naturally deduplicate by id)
  helperArray.forEach((item) => {
    uniqueItems.set(item.metadataId, item);
  });

  return {
    parsedConfigurationItemsIdsToType: dependenciesIds,
    parsedConfigurationItems: Array.from(uniqueItems.values()),
    parsedFields: fields,
  };
};

export const {
  setSingleObjectName,
  clearDocumentationDialog,
  openPredefinedStepRulesInSingleObjectScreen,
  setTab,
  handleOnPillClick,
  setActiveObjectElementsRawData,

  setObjectName,
  setObjects,

  setLatestDeployment,
  setDependenciesConfigurationItem,

  clearDependencies,
  setSelectedDependencyContentType,
  goBackInHistory,
  openEditTag,

  setCommentsForConfiguration,
  addCommentForConfiguration,
  updateCommentForConfiguration,
  deleteCommentForConfiguration,
  setSourceCodeForConfiguration,
  setConfigurationDependenciesForRule,
  updateOrAddFieldToParsedConfigurationItems,
  addConfigurationItem,
  setConfigurationUsedByForField,
  setConfigurationUsedByForRule,
  setOpenAiDescriptionForRule,
  setShowLoader,

  setTransientObjects,
  openPredefinedConfigurationCard,
} = documentationSlice.actions;

export const selectTransientObjects = (crmOrgId: string) => (state: RootState) =>
  state.documentation.transientObjects[crmOrgId];

export const selectLoader = (crmOrgId: string) => (state: RootState) =>
  state.documentation.showLoader && !state.global.environments[crmOrgId]?.data?.documentation;

export const selectSingleObject = (state: RootState) =>
  state.documentation.singleObject?.objectTypeName;

export const selectSingleObjectActiveTab = (state: RootState) =>
  state.documentation.singleObject?.activeTab;

export const selectDependenciesConfigurationItem = (state: RootState) =>
  state.documentation.dependencies;

export const selectLatestDeployment = (elementId: string) => (state: RootState) =>
  state.documentation.sweepElementsLatestDeployments[elementId];

export const selectDependenciesHistoryLength = (state: RootState) =>
  state.documentation.dependencies?.history?.length ?? 0;

export const selectDependenciesHistory = (state: RootState) =>
  state.documentation.dependencies?.history;

export const selectObjects = (state: RootState) => state.documentation.objects;

export const selectOpenAiDescriptionForRule =
  (ruleId: string, crmOrgId: string) => (state: RootState) =>
    state.documentation.openAIDescriptions[crmOrgId]?.[ruleId];

export const selectParsedConfigurationItems = (crmOrgId: string) => (state: RootState) =>
  state.documentation.parsedConfigurationItems?.[crmOrgId];

export const selectDependencyByType =
  (type: 'dependsOnIdsForRules' | 'usedByIdsForRules' | 'usedByIdsForFields') =>
  (state: RootState) =>
    state.documentation[type];

export const selectConfigurationComments =
  (crmOrgId: string, configurationId: string) => (state: RootState) =>
    state.documentation.commentsByConfigurationId?.[crmOrgId]?.[configurationId];

export const selectConfigurationSourceCode =
  (crmOrgId: string, configurationId: string) => (state: RootState) =>
    state.documentation.sourceCodeByConfigurationId?.[crmOrgId]?.[configurationId];

export const selectDocumentationActiveObjectData = (state: RootState) =>
  state.documentation.activeObjectElementsRawData;

export const selectDocumentationActiveTabElements = (state: RootState) =>
  state.documentation.activeObjectElementsRawData?.[
    state.documentation.singleObject
      ?.activeTab as keyof typeof state.documentation.activeObjectElementsRawData
  ];

export const selectRelatedObjectFilterOptions = (state: RootState) =>
  state.documentation.relatedObjectFilterOptions;

export const selectRelatedObjectConfigurationTypeFilterOptions = (state: RootState) =>
  state.documentation.configurationTypeFilterOptions;

export default documentationSlice.reducer;
